// Dashboard.jsx - Modern Theme Compliant Dashboard
import React, { useEffect, useState } from 'react';
import { summaryApi } from '../../common';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { 
  Users, 
  Package, 
  ShoppingCart, 
  Mail,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle
} from 'lucide-react';
import { displayNGNCurrency } from '../../helpers/displayCurrency';
import moment from 'moment';
import { authFetch } from '../../helpers/authFetch';

function Dashboard() {
    const { colors } = useTheme();
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalProducts: 0,
        totalOrders: 0,
        subscribers: 0,
        totalRevenue: 0,
        pendingOrders: 0
    });
    const [loading, setLoading] = useState(true);
    const [recentOrders, setRecentOrders] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const usersRes = await authFetch(summaryApi.allUser.url, {
                method: summaryApi.allUser.method,
                
            });
            const usersData = await usersRes.json();

            const productsRes = await authFetch(summaryApi.allProducts.url, {
                method: summaryApi.allProducts.method,
                
            });
            const productsData = await productsRes.json();

            const ordersRes = await authFetch(summaryApi.AdminOrderInventory.url, {
                method: summaryApi.AdminOrderInventory.method,
                
            });
            const ordersData = await ordersRes.json();

            const subscribersRes = await authFetch(summaryApi.getNewsletterSubcription.url, {
                method: summaryApi.getNewsletterSubcription.method,
                
            });
            const subscribersData = await subscribersRes.json();

            const orders = ordersData.orders || [];
            const totalRevenue = orders.reduce((sum, order) => sum + (order.amount || 0), 0);
            const pendingOrders = orders.filter(order => 
                order.status !== 'Delivered'
            ).length;

            setStats({
                totalUsers: usersData.data?.length || 0,
                totalProducts: productsData.data?.length || 0,
                totalOrders: orders.length,
                subscribers: Array.isArray(subscribersData) ? subscribersData.length : 0,
                totalRevenue: totalRevenue,
                pendingOrders: pendingOrders
            });

            setRecentOrders(orders.slice(0, 5));

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon: Icon, trend, isPositive, accentColor, description }) => (
        <div
            className="group relative rounded-xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden cursor-pointer"
            style={{
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
            }}
        >
            <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300"
                style={{ backgroundColor: accentColor }}
            />
            
            <div className="relative z-10 space-y-4">
                <div className="flex items-start justify-between">
                    <div 
                        className="p-3 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                        style={{
                            backgroundColor: `${accentColor}20`,
                        }}
                    >
                        <Icon 
                            className="w-6 h-6" 
                            style={{ color: accentColor }}
                        />
                    </div>
                    <div 
                        className="flex items-center space-x-1 text-xs font-semibold px-2 py-1 rounded-lg transition-all"
                        style={{
                            color: isPositive ? '#10b981' : '#ef4444',
                            backgroundColor: isPositive ? `#10b98120` : `#ef444420`,
                            border: `1px solid ${isPositive ? '#10b98140' : '#ef444440'}`
                        }}
                    >
                        {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        <span>{trend}</span>
                    </div>
                </div>

                <div>
                    <p 
                        className="text-sm font-medium mb-1"
                        style={{ color: colors.textSecondary }}
                    >
                        {title}
                    </p>
                    <p 
                        className="text-3xl font-bold transition-all group-hover:opacity-80"
                        style={{ color: accentColor }}
                    >
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                    {description && (
                        <p 
                            className="text-xs mt-2 transition-all"
                            style={{ color: colors.textSecondary }}
                        >
                            {description}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    const statCards = [
        {
            title: 'Total Users',
            value: stats.totalUsers,
            icon: Users,
            trend: '+12%',
            isPositive: true,
            accentColor: colors.primary,
            description: 'Active user accounts'
        },
        {
            title: 'Products',
            value: stats.totalProducts,
            icon: Package,
            trend: '+8%',
            isPositive: true,
            accentColor: colors.accent,
            description: 'Available products'
        },
        {
            title: 'Total Orders',
            value: stats.totalOrders,
            icon: ShoppingCart,
            trend: '+24%',
            isPositive: true,
            accentColor: '#10b981',
            description: 'All time orders'
        },
        {
            title: 'Subscribers',
            value: stats.subscribers,
            icon: Mail,
            trend: '+5%',
            isPositive: true,
            accentColor: '#f59e0b',
            description: 'Email subscribers'
        },
        {
            title: 'Revenue',
            value: displayNGNCurrency(stats.totalRevenue),
            icon: DollarSign,
            trend: '+18%',
            isPositive: true,
            accentColor: '#06b6d4',
            description: 'Total earnings'
        },
        {
            title: 'Pending Orders',
            value: stats.pendingOrders,
            icon: Activity,
            trend: stats.pendingOrders > 5 ? '+10%' : '-5%',
            isPositive: stats.pendingOrders <= 5,
            accentColor: '#ef4444',
            description: 'Orders to process'
        }
    ];

    if (error) {
        return (
            <div 
                className="flex items-center gap-4 p-6 rounded-lg"
                style={{
                    backgroundColor: `#ef444420`,
                    border: `1px solid #ef4444`,
                }}
            >
                <AlertCircle size={24} style={{ color: '#ef4444' }} />
                <div>
                    <p style={{ color: '#ef4444' }} className="font-semibold">
                        Error Loading Dashboard
                    </p>
                    <p style={{ color: '#ef4444' }} className="text-sm opacity-80">
                        {error}
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-4">
                    <div 
                        className="animate-spin rounded-full h-12 w-12 border-4 border-transparent mx-auto"
                        style={{
                            borderTopColor: colors.primary,
                            borderRightColor: `${colors.primary}40`,
                        }}
                    />
                    <p style={{ color: colors.textSecondary }}>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header Section */}
            <div className="mb-8">
                <h1 
                    className="text-4xl font-bold mb-2"
                    style={{ color: colors.text }}
                >
                    Dashboard
                </h1>
                <p 
                    className="text-sm flex items-center gap-2"
                    style={{ color: colors.textSecondary }}
                >
                    <Clock size={16} />
                    Last updated: {moment().format('MMMM DD, YYYY [at] h:mm A')}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {statCards.map((card, index) => (
                    <div key={index} className="animate-slideUp" style={{ animationDelay: `${index * 50}ms` }}>
                        <StatCard {...card} />
                    </div>
                ))}
            </div>

            {/* Recent Orders Section */}
            <div 
                className="rounded-xl border overflow-hidden shadow-lg transition-all hover:shadow-2xl"
                style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                }}
            >
                <div 
                    className="p-6 border-b flex items-center justify-between"
                    style={{ borderColor: colors.border }}
                >
                    <div className="flex items-center gap-3">
                        <div 
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: `${colors.primary}20` }}
                        >
                            <Clock size={20} style={{ color: colors.primary }} />
                        </div>
                        <div>
                            <h2 
                                className="text-xl font-bold"
                                style={{ color: colors.text }}
                            >
                                Recent Orders
                            </h2>
                            <p 
                                className="text-xs"
                                style={{ color: colors.textSecondary }}
                            >
                                Last {recentOrders.length} orders
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {recentOrders.length === 0 ? (
                        <div 
                            className="flex flex-col items-center justify-center py-12 rounded-lg"
                            style={{ backgroundColor: colors.bg }}
                        >
                            <ShoppingCart size={32} style={{ color: colors.textSecondary, opacity: 0.5 }} />
                            <p style={{ color: colors.textSecondary }} className="mt-4">
                                No recent orders
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentOrders.map((order, index) => (
                                <div
                                    key={order._id}
                                    className="flex items-center justify-between p-4 rounded-lg transition-all duration-300 hover:scale-102 hover:shadow-md group/order transform"
                                    style={{
                                        backgroundColor: colors.bg,
                                        border: `1px solid ${colors.border}`,
                                    }}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 transition-all group-hover/order:scale-110"
                                                style={{ backgroundColor: colors.primary }}
                                            >
                                                {index + 1}
                                            </div>
                                            <div className="min-w-0">
                                                <p 
                                                    className="font-semibold transition-colors group-hover/order:opacity-80 truncate"
                                                    style={{ color: colors.text }}
                                                >
                                                    Order #{order._id?.slice(-8).toUpperCase()}
                                                </p>
                                                <p 
                                                    className="text-sm truncate"
                                                    style={{ color: colors.textSecondary }}
                                                >
                                                    {order.items?.length || 0} items · {order.paymentMethod || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 flex-shrink-0">
                                        <div className="text-right">
                                            <p 
                                                className="font-bold transition-colors"
                                                style={{ color: colors.primary }}
                                            >
                                                {displayNGNCurrency(order.amount)}
                                            </p>
                                            <p 
                                                className="text-xs"
                                                style={{ color: colors.textSecondary }}
                                            >
                                                {moment(order.createdAt).fromNow()}
                                            </p>
                                        </div>
                                        <span 
                                            className="text-xs px-3 py-1 rounded-full font-semibold whitespace-nowrap transition-all"
                                            style={{
                                                color: order.status === 'Delivered' ? '#10b981' : order.status === 'Shipped' ? '#3b82f6' : '#f59e0b',
                                                backgroundColor: order.status === 'Delivered' ? `#10b98120` : order.status === 'Shipped' ? `#3b82f620` : `#f59e0b20`,
                                                border: `1px solid ${order.status === 'Delivered' ? '#10b981' : order.status === 'Shipped' ? '#3b82f6' : '#f59e0b'}`
                                            }}
                                        >
                                            {order.status || 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }

                .animate-slideUp {
                    animation: slideUp 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
}

export default Dashboard;